import { ReflectUtils } from "@dooboostore/core";
const ON_INITIALIZE_METADATA_KEY = Symbol.for("simple-web-component:on-constructor");
const ON_BEFORE_CONNECTED_METADATA_KEY = Symbol.for("simple-web-component:on-before-connected");
const ON_AFTER_CONNECTED_METADATA_KEY = Symbol.for("simple-web-component:on-after-connected");
const ON_BEFORE_DISCONNECTED_METADATA_KEY = Symbol.for("simple-web-component:on-before-disconnected");
const ON_AFTER_DISCONNECTED_METADATA_KEY = Symbol.for("simple-web-component:on-after-disconnected");
const ON_BEFORE_ADOPTED_METADATA_KEY = Symbol.for("simple-web-component:on-before-adopted");
const ON_AFTER_ADOPTED_METADATA_KEY = Symbol.for("simple-web-component:on-after-adopted");
const ON_ADD_EVENT_LISTENER_METADATA_KEY = Symbol.for("simple-web-component:on-add-event-listener");
const ON_CONNECTED_BODY_METADATA_KEY = Symbol.for("simple-web-component:on-connected-body");
const ON_CONNECTED_SWC_APP_METADATA_KEY = Symbol.for("simple-web-component:on-connected-swc-app");
const ON_CONNECTED_COMPLETED_METADATA_KEY = Symbol.for("simple-web-component:on-completed");
const ATTRIBUTE_CHANGED_WILDCARD = "*";
function onConnectedBefore(arg1, arg2, arg3) {
  const decorator2 = (options, target, propertyKey) => {
    const constructor = target.constructor;
    let list = ReflectUtils.getOwnMetadata(ON_BEFORE_CONNECTED_METADATA_KEY, constructor);
    if (!list) {
      list = [];
      ReflectUtils.defineMetadata(ON_BEFORE_CONNECTED_METADATA_KEY, list, constructor);
    }
    list.push({ propertyKey, options });
  };
  if (arg2) {
    return decorator2({}, arg1, arg2);
  }
  return (target, propertyKey) => {
    decorator2(arg1 || {}, target, propertyKey);
  };
}
function onConnectedAfter(arg1, arg2, arg3) {
  const decorator2 = (options, target, propertyKey) => {
    const constructor = target.constructor;
    let list = ReflectUtils.getOwnMetadata(ON_AFTER_CONNECTED_METADATA_KEY, constructor);
    if (!list) {
      list = [];
      ReflectUtils.defineMetadata(ON_AFTER_CONNECTED_METADATA_KEY, list, constructor);
    }
    list.push({ propertyKey, options });
  };
  if (arg2) {
    return decorator2({}, arg1, arg2);
  }
  return (target, propertyKey) => {
    decorator2(arg1 || {}, target, propertyKey);
  };
}
function createLifecycleDecoratorWithOptions(metadataKey) {
  return function(optionOrTarget, propertyKey) {
    if (propertyKey) {
      const constructor = optionOrTarget.constructor;
      let list = ReflectUtils.getOwnMetadata(metadataKey, constructor);
      if (!list) {
        list = [];
        ReflectUtils.defineMetadata(metadataKey, list, constructor);
      }
      list.push({ propertyKey, order: 0 });
      return;
    }
    return (target, propertyKey2) => {
      const constructor = target.constructor;
      let list = ReflectUtils.getOwnMetadata(metadataKey, constructor);
      if (!list) {
        list = [];
        ReflectUtils.defineMetadata(metadataKey, list, constructor);
      }
      list.push({ propertyKey: propertyKey2, order: optionOrTarget?.order ?? 0 });
    };
  };
}
function onInitialize(optionOrTarget, propertyKey) {
  return createLifecycleDecoratorWithOptions(ON_INITIALIZE_METADATA_KEY)(optionOrTarget, propertyKey);
}
function onDisconnectedBefore(optionOrTarget, propertyKey) {
  return createLifecycleDecoratorWithOptions(ON_BEFORE_DISCONNECTED_METADATA_KEY)(optionOrTarget, propertyKey);
}
function onDisconnectedAfter(optionOrTarget, propertyKey) {
  return createLifecycleDecoratorWithOptions(ON_AFTER_DISCONNECTED_METADATA_KEY)(optionOrTarget, propertyKey);
}
const onDisconnected = onDisconnectedAfter;
function onAdoptedBefore(optionOrTarget, propertyKey) {
  return createLifecycleDecoratorWithOptions(ON_BEFORE_ADOPTED_METADATA_KEY)(optionOrTarget, propertyKey);
}
function onAdoptedAfter(optionOrTarget, propertyKey) {
  return createLifecycleDecoratorWithOptions(ON_AFTER_ADOPTED_METADATA_KEY)(optionOrTarget, propertyKey);
}
const onAdopted = onAdoptedAfter;
function onConnectedSwcApp(optionOrTarget, propertyKey) {
  return createLifecycleDecoratorWithOptions(ON_CONNECTED_SWC_APP_METADATA_KEY)(optionOrTarget, propertyKey);
}
function onConnectedCompleted(optionOrTarget, propertyKey) {
  return createLifecycleDecoratorWithOptions(ON_CONNECTED_COMPLETED_METADATA_KEY)(optionOrTarget, propertyKey);
}
const decorator = (options, target, propertyKey) => {
  const constructor = target.constructor;
  let list = ReflectUtils.getOwnMetadata(ON_CONNECTED_BODY_METADATA_KEY, constructor);
  if (!list) {
    list = [];
    ReflectUtils.defineMetadata(ON_CONNECTED_BODY_METADATA_KEY, list, constructor);
  }
  list.push({ propertyKey, options });
};
function onConnectedBodyShadow(optionOrTarget, propertyKey, arg3) {
  if (propertyKey) {
    return decorator({ useShadow: true }, optionOrTarget, propertyKey);
  }
  return (target, propertyKey2) => {
    decorator({ ...optionOrTarget ?? {}, useShadow: true }, target, propertyKey2);
  };
}
function onConnectedBodyLight(optionOrTarget, propertyKey, arg3) {
  if (propertyKey) {
    return decorator({ useShadow: false }, optionOrTarget, propertyKey);
  }
  return (target, propertyKey2) => {
    decorator({ ...optionOrTarget ?? {}, useShadow: false }, target, propertyKey2);
  };
}
function onConnectedBody(optionOrTarget, propertyKey, arg3) {
  if (propertyKey) {
    return decorator({}, optionOrTarget, propertyKey);
  }
  return (target, propertyKey2) => {
    decorator(optionOrTarget || {}, target, propertyKey2);
  };
}
const findAllLifecycleMetadata = (target, metadataKey) => {
  const constructor = typeof target === "function" ? target : target.constructor;
  const results = ReflectUtils.findAllMetadata(metadataKey, constructor).flat();
  return [...results].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};
const findAllOnConnectedMetadata = (target) => {
  const constructor = typeof target === "function" ? target : target.constructor;
  const results = ReflectUtils.findAllMetadata(ON_CONNECTED_BODY_METADATA_KEY, constructor).flat();
  return [...results].sort((a, b) => {
    const orderA = a.options?.order ?? 0;
    const orderB = b.options?.order ?? 0;
    return orderA - orderB;
  });
};
const findAllOnConnectedBeforeMetadata = (target) => {
  const constructor = typeof target === "function" ? target : target.constructor;
  const results = ReflectUtils.findAllMetadata(ON_BEFORE_CONNECTED_METADATA_KEY, constructor).flat();
  return [...results].sort((a, b) => (a.options?.order ?? 0) - (b.options?.order ?? 0));
};
const findAllOnConnectedAfterMetadata = (target) => {
  const constructor = typeof target === "function" ? target : target.constructor;
  const results = ReflectUtils.findAllMetadata(ON_AFTER_CONNECTED_METADATA_KEY, constructor).flat();
  return [...results].sort((a, b) => (a.options?.order ?? 0) - (b.options?.order ?? 0));
};
export {
  ATTRIBUTE_CHANGED_WILDCARD,
  ON_ADD_EVENT_LISTENER_METADATA_KEY,
  ON_AFTER_ADOPTED_METADATA_KEY,
  ON_AFTER_CONNECTED_METADATA_KEY,
  ON_AFTER_DISCONNECTED_METADATA_KEY,
  ON_BEFORE_ADOPTED_METADATA_KEY,
  ON_BEFORE_CONNECTED_METADATA_KEY,
  ON_BEFORE_DISCONNECTED_METADATA_KEY,
  ON_CONNECTED_BODY_METADATA_KEY,
  ON_CONNECTED_COMPLETED_METADATA_KEY,
  ON_CONNECTED_SWC_APP_METADATA_KEY,
  ON_INITIALIZE_METADATA_KEY,
  findAllLifecycleMetadata,
  findAllOnConnectedAfterMetadata,
  findAllOnConnectedBeforeMetadata,
  findAllOnConnectedMetadata,
  onAdopted,
  onAdoptedAfter,
  onAdoptedBefore,
  onConnectedAfter,
  onConnectedBefore,
  onConnectedBody,
  onConnectedBodyLight,
  onConnectedBodyShadow,
  onConnectedCompleted,
  onConnectedSwcApp,
  onDisconnected,
  onDisconnectedAfter,
  onDisconnectedBefore,
  onInitialize
};
//# sourceMappingURL=lifecycles.js.map
